import cx from 'classnames';
import React, {useCallback, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {MoreIcon} from '~library/svg/MoreIcon';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DuplicateIcon} from '~library/svg/DuplicateIcon';
import {selectShouldShowAdvancedOptions} from '~/SignUpSheets/reducers/blueprint/selectors';
import {MenuHeader} from './MenuHeader';
import {MenuItem} from './MenuItem';
import {Menu} from './Menu';

import {feedbackError} from '~libSass/base/_exports.sass';
import './MobileSignupOptionTools.sass';

const ToolbarContent = ({
  isMenuActive,
  toggleMenu,
  index,
  onDeleteItem,
  onDuplicateItem,
  onMoveItemUp,
  onMoveItemDown,
  canMoveUp,
  canMoveDown,
  isOnlySignupOption,
  blueprintType,
  onCopySlotsToEmptyDate,
}) => {
  const isDateTimeBlueprint = selectShouldShowAdvancedOptions({
    blueprint: {blueprint_type: blueprintType},
  });

  const onActionHandler = (action) => () => {
    action(index);
    toggleMenu();
  };

  const header = <MenuHeader toggleMenu={toggleMenu} title="Edit Day" />;

  const items = [
    <MenuItem index={index} action={onActionHandler(onDuplicateItem)} key="duplicate">
      <DuplicateIcon ratio={1.5} />
      <Typography as="span" variant="paragraph2" color="charcoal">
        {isDateTimeBlueprint ? 'Copy date and its contents' : 'Duplicate'}
      </Typography>
    </MenuItem>,
    isDateTimeBlueprint && (
      <MenuItem index={index} action={onActionHandler(onCopySlotsToEmptyDate)} key="copy">
        <DuplicateIcon ratio={1.5} />
        <Typography as="span" variant="paragraph2" color="charcoal">
          Copy signup slots to other dates
        </Typography>
      </MenuItem>
    ),
    canMoveUp && (
      <MenuItem index={index} action={onActionHandler(onMoveItemUp)} key="move-up">
        <ArrowIcon direction="up" ratio={0.75} />
        <Typography as="span" variant="paragraph2" color="charcoal">
          Move slots up
        </Typography>
      </MenuItem>
    ),
    canMoveDown && (
      <MenuItem index={index} action={onActionHandler(onMoveItemDown)} key="move-down">
        <ArrowIcon direction="down" ratio={0.75} />
        <Typography as="span" variant="paragraph2" color="charcoal">
          Move slots down
        </Typography>
      </MenuItem>
    ),
    !isOnlySignupOption && (
      <MenuItem index={index} action={onActionHandler(onDeleteItem)} type="danger" key="delete">
        <CloseLineIcon ratio={0.75} color={feedbackError} />
        <Typography as="span" variant="paragraph2" color="feedbackError">
          Delete
        </Typography>
      </MenuItem>
    ),
  ].filter(Boolean);

  return <Menu header={header} items={items} menuVisible={isMenuActive} toggleMenu={toggleMenu} />;
};

export const MobileSignupOptionTools = ({
  index,
  onDeleteItem,
  onDuplicateItem,
  onMoveItemUp,
  onMoveItemDown,
  canMoveUp,
  canMoveDown,
  isOnlySignupOption,
  blueprintType,
  onCopySlotsToEmptyDate,
  inline = false,
}) => {
  const [isMenuActive, setIsMenuActive] = useState(false);

  const toggleMenu = useCallback(() => {
    setIsMenuActive((prev) => !prev);
  }, []);

  const Content = useMemo(
    () => (
      <ToolbarContent
        isMenuActive={isMenuActive}
        toggleMenu={toggleMenu}
        index={index}
        onDeleteItem={onDeleteItem}
        onDuplicateItem={onDuplicateItem}
        onMoveItemUp={onMoveItemUp}
        onMoveItemDown={onMoveItemDown}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        isOnlySignupOption={isOnlySignupOption}
        blueprintType={blueprintType}
        onCopySlotsToEmptyDate={onCopySlotsToEmptyDate}
      />
    ),
    [
      isMenuActive,
      toggleMenu,
      index,
      onDeleteItem,
      onDuplicateItem,
      onMoveItemUp,
      onMoveItemDown,
      canMoveUp,
      canMoveDown,
      isOnlySignupOption,
      blueprintType,
      onCopySlotsToEmptyDate,
    ]
  );

  return (
    <div className={cx('mobile-menu', {'mobile-menu__inline': inline})}>
      <Button
        variant="unstyled-button"
        data-qa-id="slot-toolbar-menu-mobile"
        onClick={toggleMenu}
        className="mobile-menu__button"
      >
        <MoreIcon ratio={0.75} color="charcoal" className="builder__menu-icon" />
      </Button>
      {createPortal(
        <>
          {isMenuActive && (
            <Button
              variant="unstyled-button"
              className="mobile-menu__overlay"
              onClick={toggleMenu}
              data-qa-id="signup-option-mobile-menu-overlay"
            />
          )}
          {Content}
        </>,
        document.body,
        'mobile-signup-option-tools__inline'
      )}
    </div>
  );
};
